import { Flex, Icon, VStack, Box, IconButton, Text } from '@cad-arc';
import { format, isSameDay, isSameYear, parseJSON } from 'date-fns';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import { ConversationMessageView } from '@mark43/cad-api';
import { useMemo } from 'react';
import { FlagRenderer } from '../../common/components/flag-renderer/flag-renderer';
import { useMessagingStore } from '../hooks/use-messaging-store';
import { useUpdateBookmark } from '../hooks/resource/use-update-bookmark';
import { useOpenEvent } from '../../common/hooks/use-open-event';
import { FR_EVENT_DETAILS_TABS } from '../../fr-event-details-page/constants';
import styles from './message-row.module.css';
import { colors } from '../../colors/colors';

const NON_BREAKING_SPACE = '\xa0';

export const formatMessageTime = (date: Date, now: Date): string => {
    if (isSameDay(date, now)) return format(date, 'HH:mm:ss');
    if (isSameYear(date, now)) return format(date, `LL/dd${NON_BREAKING_SPACE}HH:mm`);
    return format(date, `LL/dd/yyyy${NON_BREAKING_SPACE}HH:mm`);
};

export const MessageRow = observer(
    ({
        message,
        getSenderColor,
    }: {
        message: ConversationMessageView;
        getSenderColor: (message: ConversationMessageView) => string;
    }) => {
        const messageSenderColor = useMemo(
            () => getSenderColor(message),
            [getSenderColor, message]
        );

        const { selectedConversationKey } = useMessagingStore();

        const { updateBookmark } = useUpdateBookmark(selectedConversationKey);
        const handleOpenEvent = useOpenEvent();

        return (
            <VStack
                display="inline-flex"
                alignItems="flex-start"
                padding="2"
                spacing="0"
                margin="0"
                borderRadius="md"
                backgroundColor={
                    message.isUrgent
                        ? 'var(--arc-colors-urgent-default)'
                        : colors.messagesBackground
                }
                className={styles['message-row']}
            >
                <Flex justifyContent="space-between" alignItems="baseline" width="100%" gap="4">
                    <Flex alignItems="baseline" gap="2">
                        <Text
                            fontWeight="bold"
                            lineHeight="short"
                            style={{
                                color: message.isUrgent
                                    ? 'var(--arc-colors-urgent-content)'
                                    : `var(--arc-colors-${messageSenderColor}`,
                            }}
                        >
                            {message.sender.label}
                        </Text>
                        {message.sender.entityType !== 'USER' && (
                            <Text
                                color="tertiary"
                                fontSize="sm"
                                lineHeight="short"
                                style={{
                                    color: message.isUrgent
                                        ? 'var(--arc-colors-urgent-content)'
                                        : undefined,
                                }}
                            >
                                ({message.senderUserLabel})
                            </Text>
                        )}
                    </Flex>
                    <Text
                        fontSize="sm"
                        lineHeight="short"
                        style={{
                            color: message.isUrgent
                                ? 'var(--arc-colors-urgent-content)'
                                : 'var(--arc-colors-text-primary)',
                        }}
                    >
                        {message.createdDateUtc &&
                            formatMessageTime(parseJSON(message.createdDateUtc), new Date())}
                    </Text>
                </Flex>
                <Flex className="text" justifyContent="space-between" width="100%" gap="4">
                    <Box
                        whiteSpace="pre-wrap"
                        overflowWrap="anywhere"
                        color={
                            message.isUrgent
                                ? 'var(--arc-colors-urgent-content)'
                                : 'var(--arc-colors-text-primary)'
                        }
                    >
                        <FlagRenderer
                            text={message.text}
                            agencyEventNumberToIdMap={message.agencyEventNumberToIdMap}
                            onAgencyEventNumberClick={({ eventId }) => {
                                handleOpenEvent({
                                    frEventDetailsTabName: FR_EVENT_DETAILS_TABS.NARRATIVE,
                                    eventId,
                                });
                            }}
                        />
                    </Box>
                    <Flex width={message.isUrgent ? '58px' : '40px'} justifyContent="end">
                        {message.isUrgent && (
                            <Flex h="6" alignItems="center">
                                <Icon
                                    style={{ height: '20px' }} //20px so it lines up with the bookmark icon
                                    color="var(--arc-colors-urgent-content)"
                                    icon="ExclamationPoint"
                                />
                            </Flex>
                        )}
                        <IconButton
                            data-testid={
                                message.isBookmarked ? 'is-bookmarked' : 'is-not-bookmarked'
                            }
                            style={{
                                height: 'var(--arc-space-6)',
                                color: message.isUrgent
                                    ? 'var(--arc-colors-urgent-content)'
                                    : undefined,
                            }}
                            _hover={{
                                backgroundColor: message.isUrgent
                                    ? 'var(--arc-colors-urgent-hover) !important'
                                    : undefined,
                            }}
                            icon={message.isBookmarked ? 'Bookmark' : 'BookmarkEmpty'}
                            variant="ghost"
                            aria-label="Toggle bookmark"
                            onClick={() => {
                                updateBookmark(message, !message.isBookmarked);
                            }}
                            className={classNames(styles['bookmark'], {
                                [styles['hidden']]: !message.isBookmarked,
                            })}
                        />
                    </Flex>
                </Flex>
            </VStack>
        );
    }
);
